import type { ContentCardItem } from '@content-search/domain';
import { InlineAudioPlayer } from '@core/components';
import { ButtonType, Text, IconButton, IconSize, ChevronDown, ChevronUp } from '@soteria/design-system-ve';

import * as contentCardStyles from './ContentCard.css';
import * as styles from './ConversationContextCard.css';

export interface ConversationContextCardProps {
  item: ContentCardItem;
  isSelected?: boolean;
  isOriginalSelection?: boolean;
  alignment: 'left' | 'right';
  onClick?: () => void;
  mediaServiceUrl?: string;
}

// Component for messaging-style content summary with chat-like layout

function MessagingStyleContentSummary({ item, alignment }: { item: ContentCardItem; alignment: 'left' | 'right' }) {
  // Determine if content is safe or harmful for border color and position based on alignment
  const isSafeContent = item.classificationResult !== 'rejected';

  // For left-aligned messages, put the border on the right side
  // For right-aligned messages, put the border on the left side (default)
  let borderClass = '';

  if (alignment === 'left') {
    borderClass = isSafeContent ? contentCardStyles.safeContentBorderRight : contentCardStyles.unsafeContentBorderRight;
  } else {
    borderClass = isSafeContent ? contentCardStyles.safeContentBorder : contentCardStyles.unsafeContentBorder;
  }

  return (
    <div className={`${contentCardStyles.searchContentSummary} ${borderClass}`}>
      {/* Primary Message Content */}
      <div className={contentCardStyles.searchTranscriptionContent}>
        <div className={`${contentCardStyles.textContainer} ${contentCardStyles.contentBoxContainer}`} style={{ marginBottom: 0 }}>
          <div className={contentCardStyles.textWrapper} style={{ position: 'relative' }}>
            <Text className={contentCardStyles.searchTranscriptionText}>{item.text || 'No content available'}</Text>
          </div>
        </div>
      </div>

      {/* Labels removed for conversation context - cleaner messaging view */}
    </div>
  );
}

/**
 * ConversationContextCard component for displaying messages in conversation context
 * Shows only the inner content (SearchStyleContentSummary) without the outer Card container
 */
// eslint-disable-next-line complexity
export function ConversationContextCard({
  item,
  isSelected = false,
  isOriginalSelection = false,
  alignment,
  onClick,
  mediaServiceUrl,
}: ConversationContextCardProps) {
  const containerClass = `${styles.contextCardContainer} ${
    alignment === 'left' ? styles.leftAligned : styles.rightAligned
  } ${isSelected ? styles.selectedCard : ''}`;

  const cardClass = `${styles.contextCard} ${alignment === 'left' ? styles.leftCard : styles.rightCard} ${isSelected ? styles.selectedCardInner : ''}`;

  const handleCardClick = () => {
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <div
      aria-label={`Message from ${(() => {
        const username = item.subjectOwner?.username;
        const userId = item.subjectOwner?.userId;

        if (username && userId) {
          return `${username} (${userId})`;
        } else if (username) {
          return username;
        } else if (userId) {
          return userId;
        } else {
          return 'Unknown User';
        }
      })()}: ${item.text?.substring(0, 100) || 'No content available'}${item.text && item.text.length > 100 ? '...' : ''}`}
      className={isOriginalSelection ? styles.originalSelectedMessage : ''}
      data-is-original-selection={isOriginalSelection} // Add data attribute for debugging
      data-message-id={item.id}
      style={{ marginBottom: '16px' }}
    >
      {/* Username and timestamp above message - container matches message bubble width and alignment */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          maxWidth: '60%', // Match message bubble max width
          minWidth: '25%', // Match message bubble min width
          width: 'auto', // Allow width to be determined by content
          marginLeft: alignment === 'right' ? 'auto' : '0', // Align to right for right-aligned messages
        }}
      >
        {/* Username and timestamp content */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1, // Take available space
          }}
        >
          <span
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            {(() => {
              const username = item.subjectOwner?.username;
              const userId = item.subjectOwner?.userId;

              if (username && userId) {
                return `${username} (${userId})`;
              } else if (username) {
                return username;
              } else if (userId) {
                return userId;
              } else {
                return 'Unknown User';
              }
            })()}
          </span>
          {item.timestamp && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: '400',
                color: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              {(() => {
                const date = new Date(item.timestamp);

                // Always show date and time
                return date.toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              })()}
            </span>
          )}
        </div>

        {/* Inline Audio Player in the username/timestamp area */}
        {(item.contentUrl || item.audioFileUrl) && (
          <div
            role="button"
            tabIndex={0}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <InlineAudioPlayer audioId={item.id} audioUrl={item.contentUrl || item.audioFileUrl || ''} mediaServiceUrl={mediaServiceUrl} />
          </div>
        )}
      </div>

      {/* Message container with bubble */}
      <div className={containerClass} role="button" tabIndex={0} onClick={handleCardClick} onKeyDown={handleKeyDown}>
        {/* Message bubble arrow */}
        <div className={`${styles.bubbleArrow} ${alignment === 'left' ? styles.leftArrow : styles.rightArrow}`} />

        {/* Only the inner content (MessagingStyleContentSummary) without Card container */}
        <div className={cardClass}>
          <MessagingStyleContentSummary alignment={alignment} item={item} />
        </div>
      </div>
    </div>
  );
}

export interface LoadMoreButtonProps {
  direction: 'up' | 'down';
  onClick: () => void;
  isLoading?: boolean;
}

/**
 * LoadMoreButton component for loading additional conversation context
 */
export function LoadMoreButton({ direction, onClick, isLoading = false }: LoadMoreButtonProps) {
  return (
    <div className={styles.loadMoreContainer}>
      <IconButton
        buttonType={ButtonType.Tertiary}
        disabled={isLoading}
        icon={direction === 'up' ? ChevronUp : ChevronDown}
        iconSize={IconSize.XSmall}
        onClick={onClick}
      />
    </div>
  );
}
